'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/components/ui/utils';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import type { ProcessStageView } from '../lib/process-stage-constants';
import ProcessStageModal from './ProcessStageModal';
import ProcessStageVideo from './ProcessStageVideo';

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BRAND_RED = '#EC1C24';

function stageAnim(
  entered: boolean,
  name: string,
  duration: string,
  delay: string,
  easing = EASE,
): string {
  return entered ? `${name} ${duration} ${easing} ${delay} both` : 'none';
}

interface ProcessSectionProps {
  stage: ProcessStageView;
}

export default function ProcessSection({ stage }: ProcessSectionProps) {
  const t = useTranslations('ourProces.section');
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const isEven = stage.id % 2 === 0;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setIsInView(true);
      setHasEntered(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const intersecting = entry.isIntersecting;
        setIsInView(intersecting);
        if (intersecting) {
          requestAnimationFrame(() => setHasEntered(true));
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -8% 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const panelBg = isEven ? 'bg-[#EAE7E1]' : 'bg-[#F0EDE8]';
  const contentReveal = isEven ? 'process-content-reveal-right' : 'process-content-reveal-left';

  return (
    <>
      <div
        ref={ref}
        className={cn(
          'relative flex min-h-0 overflow-hidden lg:min-h-svh',
          isEven ? 'flex-col lg:flex-row-reverse' : 'flex-col lg:flex-row',
        )}
      >
        <div className="pointer-events-none absolute left-0 right-0 top-0 z-30 h-[3px] overflow-hidden">
          <div
            className="h-full"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${BRAND_RED} 20%, ${BRAND_RED} 80%, transparent 100%)`,
              animation: stageAnim(hasEntered, 'process-section-sweep', '1s', '0.1s', 'ease'),
            }}
          />
        </div>

        <div className="relative min-h-[38svh] flex-1 overflow-hidden bg-[#0c0c0c] sm:min-h-[42svh] md:min-h-[45svh] lg:min-h-svh">
          <div
            className={cn(
              'process-video-clip-reveal absolute inset-0 will-change-[clip-path]',
              isEven ? 'process-video-clip-reveal--right' : 'process-video-clip-reveal--left',
              hasEntered && 'is-active',
              'motion-reduce:![clip-path:unset] motion-reduce:!animate-none',
            )}
          >
            <div
              className={cn(
                'process-video-clip-inner absolute inset-0 overflow-hidden',
                hasEntered && 'is-active',
                'motion-reduce:!animate-none',
              )}
            >
              {stage.videoSrc ? (
                <>
                  <ProcessStageVideo src={stage.videoSrc} isActive={isInView} />
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20"
                    aria-hidden
                  />
                </>
              ) : null}
            </div>
          </div>

          <div
            className="absolute left-0 top-0 z-[5] h-[3px] transition-[width] duration-[1600ms] ease-out"
            style={{
              width: hasEntered ? '100%' : '0%',
              transitionDelay: hasEntered ? '0.35s' : '0s',
              background: BRAND_RED,
            }}
          />

          <div
            className="absolute bottom-4 right-4 flex max-w-[calc(100%-2rem)] items-center gap-2 sm:bottom-7 sm:right-7 sm:max-w-none"
            style={{ animation: stageAnim(hasEntered, 'process-fade-in', '0.7s', '0.65s', 'ease') }}
          >
            <div
              className="h-1.5 w-1.5 rounded-full"
              style={{
                background: stage.color,
                boxShadow: `0 0 8px ${stage.color}`,
              }}
            />
            <span className="truncate text-[7px] font-medium uppercase tracking-[0.25em] text-white/60 sm:text-[8px] sm:tracking-[0.3em]">
              {stage.subtitle}
            </span>
          </div>
        </div>

        <div
          className={cn(
            'relative flex w-full shrink-0 flex-col justify-center px-5 py-10 sm:px-8 sm:py-12 md:px-10 md:py-14 lg:w-[460px] lg:px-[52px] lg:py-[72px]',
            panelBg,
          )}
          style={{
            animation: stageAnim(hasEntered, 'process-stage-enter', '0.85s', '0.15s', EASE),
          }}
        >
          <div
            className={cn(
              'absolute bottom-[15%] top-[15%] hidden w-[3px] transition-opacity duration-700 md:block',
              isEven ? 'right-0' : 'left-0',
            )}
            style={{
              opacity: hasEntered ? 0.65 : 0,
              transitionDelay: hasEntered ? '0.4s' : '0s',
              background: `linear-gradient(180deg, transparent, ${BRAND_RED} 30%, ${BRAND_RED} 70%, transparent)`,
            }}
          />

          <h2
            className="mb-3 text-[clamp(1.375rem,4.5vw,2.25rem)] font-semibold leading-[1.12] tracking-tight text-[#111111] sm:mb-3.5 sm:leading-[1.1]"
            style={{ animation: stageAnim(hasEntered, contentReveal, '0.8s', '0.28s') }}
          >
            {stage.title}
          </h2>

          <div
            className="mb-5 h-[3px] w-8 origin-left rounded-sm"
            style={{
              background: BRAND_RED,
              animation: stageAnim(hasEntered, 'process-expand-line', '0.6s', '0.42s'),
            }}
          />

          <p
            className="mb-6 text-[13px] font-light leading-[1.85] text-black/50 sm:mb-8 sm:text-sm sm:leading-[1.9]"
            style={{ animation: stageAnim(hasEntered, 'process-slide-in-up', '0.75s', '0.5s') }}
          >
            {stage.shortDesc}
          </p>

          {stage.stats.length > 0 ? (
            <div
              className="mb-7 grid grid-cols-3 gap-px overflow-hidden rounded-[8px] shadow-[0_2px_12px_rgba(0,0,0,0.07)] sm:mb-9 sm:rounded-[10px]"
              style={{ background: 'rgba(0,0,0,0.07)' }}
            >
              {stage.stats.map((stat, index) => (
                <div
                  key={`${stat.label}-${stat.value}`}
                  className="bg-white px-1.5 py-3 text-center sm:px-2.5 sm:py-[18px]"
                  style={{
                    animation: stageAnim(
                      hasEntered,
                      'process-stat-rise',
                      '0.65s',
                      `${0.62 + index * 0.12}s`,
                    ),
                  }}
                >
                  <div className="mb-1 text-[14px] font-semibold leading-tight text-[#111111] sm:mb-1.5 sm:text-[17px]">
                    {stat.value}
                  </div>
                  <div className="text-[6.5px] font-medium uppercase leading-tight tracking-[0.1em] text-black/[0.32] sm:text-[8px] sm:tracking-[0.15em]">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div
            style={{ animation: stageAnim(hasEntered, 'process-cta-bounce-in', '0.7s', '0.95s') }}
          >
            <SwapTextButton
              text={stage.buttonText?.trim() || t('knowMore')}
              hoverText={t('knowMoreHover')}
              onClick={() => setModalOpen(true)}
              className="shadow-[0_4px_16px_rgba(236,28,36,0.28)]"
            />
          </div>
        </div>
      </div>

      {modalOpen ? <ProcessStageModal stage={stage} onClose={() => setModalOpen(false)} /> : null}
    </>
  );
}
